import { EBrandKitTabs, StoreBodyBlock } from 'constants/enums';
import {
  ETemplateStudioSidebarBlock,
  TemplateStudioBlock
} from 'state/template-studio.state.service';
import { collectNestedFormErrorPaths } from 'utils/reactHookFormValidation.utils';

export type FieldLocation =
  | { type: 'brandKit'; tab: EBrandKitTabs; fieldName: string; scrollTarget?: string }
  | { type: 'sidebar'; block: TemplateStudioBlock; fieldName: string; scrollTarget?: string }
  | { type: 'header'; fieldName: string; scrollTarget?: string };

interface FieldMeta {
  displayName: string;
  toastDescription: string;
}

const FIELD_META: Record<string, FieldMeta> = {
  'brandKit.logo': {
    displayName: 'Logo',
    toastDescription: 'Please upload a logo to continue'
  },
  'brandKit.backgroundImageMobile': {
    displayName: 'Background image (mobile)',
    toastDescription: 'Please upload a mobile background image to continue'
  },
  'brandKit.backgroundImageDesktop': {
    displayName: 'Background image (desktop)',
    toastDescription: 'Please upload a desktop background image to continue'
  },
  'brandKit.fontFamily': {
    displayName: 'Font family',
    toastDescription: 'Please select a font family to continue'
  },
  'brandKit.buttonColor': {
    displayName: 'Button color',
    toastDescription: 'Please set a button color to continue'
  },
  'brandKit.buttonColor.colorOne': {
    displayName: 'Button color',
    toastDescription: 'Please set a button color to continue'
  },
  'brandKit.buttonTextColor': {
    displayName: 'Button text color',
    toastDescription: 'Please set a button text color to continue'
  },
  'externalButtons.addToHomeScreenButton.iconImage': {
    displayName: 'Icon image',
    toastDescription: 'Please upload an icon image to continue'
  },
  'externalButtons.addToHomeScreenButton.shortcutName': {
    displayName: 'Store title',
    toastDescription: 'Please enter a store title to continue'
  },
  'externalButtons.backToGameButton.buttonImage': {
    displayName: 'Button image',
    toastDescription: 'Please upload a button image to continue'
  },
  'externalButtons.backToGameButton.position': {
    displayName: 'Button position',
    toastDescription: 'Please select a button position to continue'
  },
  'externalButtons.verticalOffset': {
    displayName: 'Vertical offset',
    toastDescription: 'Please set a valid vertical offset to continue'
  },
  templateName: {
    displayName: 'Template name',
    toastDescription: 'Please enter a template name to continue'
  },
  'header.backgroundOpacity': {
    displayName: 'Header background opacity',
    toastDescription: 'Please set header background opacity to continue'
  },
  'header.textColor.colorOne': {
    displayName: 'Header text color',
    toastDescription: 'Please set header text color to continue'
  }
};

const FIELD_LOCATION_MAP: Record<string, FieldLocation> = {
  templateName: {
    type: 'header',
    fieldName: 'templateName',
    scrollTarget: 'templateName'
  },
  'brandKit.logo': {
    type: 'brandKit',
    tab: EBrandKitTabs.BRANDING,
    fieldName: 'brandKit.logo',
    scrollTarget: 'BrandKit-LogoUpload'
  },
  'brandKit.backgroundImageMobile': {
    type: 'brandKit',
    tab: EBrandKitTabs.BRANDING,
    fieldName: 'brandKit.backgroundImageMobile',
    scrollTarget: 'BrandKit-BackgroundImageMobileUpload'
  },
  'brandKit.backgroundImageDesktop': {
    type: 'brandKit',
    tab: EBrandKitTabs.BRANDING,
    fieldName: 'brandKit.backgroundImageDesktop',
    scrollTarget: 'BrandKit-BackgroundImageDesktopUpload'
  },
  'brandKit.fontFamily': {
    type: 'brandKit',
    tab: EBrandKitTabs.BRANDING,
    fieldName: 'brandKit.fontFamily',
    scrollTarget: 'BrandKit-FontFamily'
  },
  'brandKit.buttonColor': {
    type: 'brandKit',
    tab: EBrandKitTabs.BRANDING,
    fieldName: 'brandKit.buttonColor'
  },
  'brandKit.buttonTextColor': {
    type: 'brandKit',
    tab: EBrandKitTabs.BRANDING,
    fieldName: 'brandKit.buttonTextColor',
    scrollTarget: 'BrandKit-ButtonTextColor'
  },
  'externalButtons.addToHomeScreenButton.iconImage': {
    type: 'brandKit',
    tab: EBrandKitTabs.EXTERNAL_BUTTONS,
    fieldName: 'externalButtons.addToHomeScreenButton.iconImage',
    scrollTarget: 'BrandKit-IconImageUpload'
  },
  'externalButtons.addToHomeScreenButton.shortcutName': {
    type: 'brandKit',
    tab: EBrandKitTabs.EXTERNAL_BUTTONS,
    fieldName: 'externalButtons.addToHomeScreenButton.shortcutName',
    scrollTarget: 'BrandKit-ShortcutName'
  },
  'externalButtons.backToGameButton.buttonImage': {
    type: 'brandKit',
    tab: EBrandKitTabs.EXTERNAL_BUTTONS,
    fieldName: 'externalButtons.backToGameButton.buttonImage',
    scrollTarget: 'BrandKit-BackToGameButtonImageUpload'
  },
  'externalButtons.backToGameButton.position': {
    type: 'brandKit',
    tab: EBrandKitTabs.EXTERNAL_BUTTONS,
    fieldName: 'externalButtons.backToGameButton.position',
    scrollTarget: 'BrandKit-BackToGameButtonPosition'
  },
  'externalButtons.verticalOffset': {
    type: 'brandKit',
    tab: EBrandKitTabs.EXTERNAL_BUTTONS,
    fieldName: 'externalButtons.verticalOffset',
    scrollTarget: 'BrandKit-VerticalOffset'
  },
  'header.backgroundOpacity': {
    type: 'sidebar',
    block: 'header' as TemplateStudioBlock,
    fieldName: 'header.backgroundOpacity',
    scrollTarget: 'header-backgroundOpacity'
  },
  'header.textColor': {
    type: 'sidebar',
    block: 'header' as TemplateStudioBlock,
    fieldName: 'header.textColor',
    scrollTarget: 'header-textColor'
  },
  'footer.backgroundOpacity': {
    type: 'sidebar',
    block: 'footer' as TemplateStudioBlock,
    fieldName: 'footer.backgroundOpacity'
  },
  'footer.textColor': {
    type: 'sidebar',
    block: 'footer' as TemplateStudioBlock,
    fieldName: 'footer.textColor'
  },
  'promotion.selectedDesignId': {
    type: 'sidebar',
    block: StoreBodyBlock.specialOffers,
    fieldName: 'promotion.selectedDesignId'
  },
  'bundle.selectedDesignId': {
    type: 'sidebar',
    block: StoreBodyBlock.bundles,
    fieldName: 'bundle.selectedDesignId'
  },
  'popup.selectedDesignId': {
    type: 'sidebar',
    block: 'popup' as TemplateStudioBlock,
    fieldName: 'popup.selectedDesignId'
  },
  'rollingOffers.selectedDesignId': {
    type: 'sidebar',
    block: StoreBodyBlock.rollingOffers,
    fieldName: 'rollingOffers.selectedDesignId'
  },
  'buyOneGetMany.selectedDesignId': {
    type: 'sidebar',
    block: StoreBodyBlock.buyOneGetMany,
    fieldName: 'buyOneGetMany.selectedDesignId'
  },
  'buyOneGetMany.freeTilesLayout': {
    type: 'sidebar',
    block: StoreBodyBlock.buyOneGetMany,
    fieldName: 'buyOneGetMany.freeTilesLayout'
  },
  'progressBar.selectedDesignId': {
    type: 'sidebar',
    block: StoreBodyBlock.progressBars,
    fieldName: 'progressBar.selectedDesignId'
  },
  'dailyBonusBadge.selectedDesignId': {
    type: 'sidebar',
    block: StoreBodyBlock.dailyBonusBadge,
    fieldName: 'dailyBonusBadge.selectedDesignId'
  },
  'dailyBonus.selectedBadgeDesignId': {
    type: 'sidebar',
    block: StoreBodyBlock.dailyBonusBadge,
    fieldName: 'dailyBonus.selectedBadgeDesignId'
  },
  'dailyBonus.selectedPopupDesignId': {
    type: 'sidebar',
    block: StoreBodyBlock.dailyBonusBadge,
    fieldName: 'dailyBonus.selectedPopupDesignId'
  },
  'triggeredPopups.selectedDesignId': {
    type: 'sidebar',
    block: ETemplateStudioSidebarBlock.TRIGGERED_POPUPS_POPUP,
    fieldName: 'triggeredPopups.selectedDesignId'
  },
  'triggeredPopups.selectedBadgeDesignId': {
    type: 'sidebar',
    block: StoreBodyBlock.triggeredPopups,
    fieldName: 'triggeredPopups.selectedBadgeDesignId'
  },
  'rewardCalendar.selectedDesignId': {
    type: 'sidebar',
    block: ETemplateStudioSidebarBlock.REWARD_CALENDAR_POPUP,
    fieldName: 'rewardCalendar.selectedDesignId'
  },
  'rewardCalendar.selectedBadgeDesignId': {
    type: 'sidebar',
    block: StoreBodyBlock.rewardCalendar,
    fieldName: 'rewardCalendar.selectedBadgeDesignId'
  }
};

function getFieldMeta(fieldPath: string): FieldMeta {
  if (FIELD_META[fieldPath]) return FIELD_META[fieldPath];

  for (const [mapPath, meta] of Object.entries(FIELD_META)) {
    if (fieldPath.startsWith(mapPath)) return meta;
  }

  const fallbackName = camelToTitle(fieldPath.split('.').pop() ?? fieldPath);
  return {
    displayName: fallbackName,
    toastDescription: `Please fill in the ${fallbackName.toLowerCase()} to continue`
  };
}

function camelToTitle(s: string): string {
  return s
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export function getFieldLocation(fieldPath: string): FieldLocation | undefined {
  if (FIELD_LOCATION_MAP[fieldPath]) return FIELD_LOCATION_MAP[fieldPath];

  for (const [mapPath, location] of Object.entries(FIELD_LOCATION_MAP)) {
    if (fieldPath.startsWith(mapPath)) return location;
  }
  return undefined;
}

export interface ValidationResult {
  missingFieldPaths: string[];
  firstFieldLocation: FieldLocation | undefined;
  toastMessage: string;
  toastDescriptions: string[];
}

export function buildValidationResult(
  errors: Record<string, unknown>
): ValidationResult {
  const missingFieldPaths = collectNestedFormErrorPaths(errors);
  const firstFieldLocation =
    missingFieldPaths.length > 0
      ? getFieldLocation(missingFieldPaths[0])
      : undefined;

  return {
    missingFieldPaths,
    firstFieldLocation,
    toastMessage: 'Please complete all required fields before saving',
    toastDescriptions: missingFieldPaths.map(
      (p) => getFieldMeta(p).toastDescription
    )
  };
}
